import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth0 } from '@auth0/auth0-react';
import { BACKEND_URL } from '../utils/constants';
import { useOrgApi } from '../utils/orgApi';
import { ConstructionLoader } from './ConstructionLoader';

/**
 * The audit log table (enterprise batch 9).
 *
 * scope="org":   the caller's firm, from /api/org/activity (Team page, admins).
 * scope="admin": every firm, from /api/admin/activity (founders' Activity page).
 *
 * Read-only: the log cannot be edited or cleared from the app.
 */

export interface ActivityEvent {
  id: string;
  created_at: string | null;
  org_id: string | null;
  org_name?: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  action_label: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  summary: string;
  ip_address: string | null;
}

interface Person {
  id: string;
  email: string;
}

interface Props {
  scope: 'org' | 'admin';
  /** Org scope: the firm's members, for the Person filter. */
  people?: Person[];
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'deals', label: 'Deals' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'team', label: 'Team' },
  { value: 'settings', label: 'Settings' },
  { value: 'access', label: 'Sign-ins' },
  { value: 'billing', label: 'Billing' },
  { value: 'platform', label: 'UnderwritRE staff' },
];

const PAGE_SIZE = 50;

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

interface Filters {
  category: string;
  actor: string;
  since: string;
  until: string;
  email: string;
  firm: string;
}

const EMPTY: Filters = { category: '', actor: '', since: '', until: '', email: '', firm: '' };

function queryString(f: Filters, scope: 'org' | 'admin', extra?: Record<string, string>): string {
  const p = new URLSearchParams();
  if (f.category) p.set('category', f.category);
  if (f.since) p.set('since', f.since);
  if (f.until) p.set('until', f.until);
  if (scope === 'org' && f.actor) p.set('actor', f.actor);
  if (scope === 'admin' && f.email.trim()) p.set('email', f.email.trim());
  if (scope === 'admin' && f.firm.trim()) p.set('firm', f.firm.trim());
  if (extra) {
    Object.keys(extra).forEach((k) => p.set(k, extra[k]));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

const ActivityLog: React.FC<Props> = ({ scope, people }) => {
  const api = useOrgApi();
  const { getAccessTokenSilently } = useAuth0();
  const base = scope === 'org' ? '/org/activity' : '/admin/activity';

  const [filters, setFilters] = useState<Filters>(EMPTY);
  // Text boxes (founder view) apply on Enter or Search, not on every keystroke.
  const [draft, setDraft] = useState<{ email: string; firm: string }>({ email: '', firm: '' });
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: Filters) => {
    setLoading(true);
    setError(null);
    const r = await api<{ events: ActivityEvent[]; next_before: string | null }>(
      'GET', `${base}${queryString(f, scope, { limit: String(PAGE_SIZE) })}`
    );
    if (r.ok) {
      setEvents(r.data?.events || []);
      setNext(r.data?.next_before || null);
    } else {
      setEvents([]);
      setNext(null);
      setError(r.error || 'Could not load the activity log.');
    }
    setLoading(false);
  }, [api, base, scope]);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    const r = await api<{ events: ActivityEvent[]; next_before: string | null }>(
      'GET', `${base}${queryString(filters, scope, { limit: String(PAGE_SIZE), before: next })}`
    );
    setLoadingMore(false);
    if (!r.ok) {
      setError(r.error || 'Could not load more.');
      return;
    }
    setEvents((prev) => prev.concat(r.data?.events || []));
    setNext(r.data?.next_before || null);
  };

  const downloadCsv = async () => {
    setDownloading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const res = await fetch(`${BACKEND_URL}/api${base}.csv${queryString(filters, scope)}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = `Download failed (${res.status}).`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch {
          // not JSON
        }
        setError(msg);
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/.exec(disposition);
      const name = match ? match[1] : 'underwritre-activity.csv';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Could not download the file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const applyText = () => set({ email: draft.email, firm: draft.firm });
  const filtered = Object.keys(filters).some((k) => (filters as any)[k]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Select
          size="small"
          displayEmpty
          value={filters.category}
          onChange={(e) => set({ category: e.target.value as string })}
          sx={{ minWidth: 150 }}
          inputProps={{ 'aria-label': 'activity type' }}
        >
          <MenuItem value="">All activity</MenuItem>
          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </Select>
        {scope === 'org' && (
          <Select
            size="small"
            displayEmpty
            value={filters.actor}
            onChange={(e) => set({ actor: e.target.value as string })}
            sx={{ minWidth: 200 }}
            inputProps={{ 'aria-label': 'person' }}
          >
            <MenuItem value="">Everyone</MenuItem>
            {(people || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.email}</MenuItem>)}
          </Select>
        )}
        {scope === 'admin' && (
          <>
            <TextField
              size="small"
              label="Firm"
              value={draft.firm}
              onChange={(e) => setDraft({ ...draft, firm: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') applyText(); }}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              label="Email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') applyText(); }}
              sx={{ width: 200 }}
            />
            <Button size="small" variant="outlined" onClick={applyText} sx={{ height: 40 }}>Search</Button>
          </>
        )}
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={filters.since}
          onChange={(e) => set({ since: e.target.value })}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={filters.until}
          onChange={(e) => set({ until: e.target.value })}
          sx={{ width: 160 }}
        />
        {filtered && (
          <Button size="small" onClick={() => { setFilters(EMPTY); setDraft({ email: '', firm: '' }); }}>
            Clear filters
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Everything that matches the filters, as a spreadsheet">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadCsv}
              disabled={downloading}
              sx={{ height: 40 }}
            >
              {downloading ? 'Preparing...' : 'Download CSV'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><ConstructionLoader /></Box>
      ) : events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {filtered
            ? 'Nothing matches these filters.'
            : 'Nothing recorded yet. Activity is recorded from the day the log was switched on; earlier changes are not in it.'}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 170 }}>When</TableCell>
                {scope === 'admin' && <TableCell sx={{ width: 170 }}>Firm</TableCell>}
                <TableCell sx={{ width: 210 }}>Person</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.id}>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    <Tooltip title={ev.ip_address ? `IP address ${ev.ip_address}` : ''}>
                      <span>{formatWhen(ev.created_at)}</span>
                    </Tooltip>
                  </TableCell>
                  {scope === 'admin' && <TableCell sx={{ wordBreak: 'break-word' }}>{ev.org_name || ''}</TableCell>}
                  <TableCell sx={{ wordBreak: 'break-word' }}>{ev.actor_email || 'System'}</TableCell>
                  <TableCell>{ev.action_label}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{ev.target_label || ''}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{ev.summary}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {next && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Loading...' : 'Load more'}</Button>
        </Box>
      )}
    </Box>
  );
};

export default ActivityLog;
